import { detectPlatform } from "../../platform.js";
import { localeForLanguage, translate, type I18nKey } from "../../lib/language.js";
import { fontStack } from "../../lib/typography.js";
import { useCalendarsStore } from "../../stores/calendars.store.js";
import { useLabelsStore } from "../../stores/labels.store.js";
import { useSettingsStore } from "../../stores/settings.store.js";
import { useTasksStore } from "../../stores/tasks.store.js";
import { useTopicsStore } from "../../stores/topics.store.js";

export const FEEDBACK_EMAIL = "glazolia@gmail.com";

function tr(key: I18nKey, vars?: Record<string, string | number | null | undefined>): string {
  return translate(useSettingsStore.getState().language, key, vars);
}

function appLocale(): string {
  return localeForLanguage(useSettingsStore.getState().language);
}

export interface FeedbackImage {
  dataUrl: string;
  name: string;
  mime: string;
}

export interface FeedbackDraft {
  note: string;
  image?: FeedbackImage | undefined;
  source?: string | undefined;
}

export async function captureDisplayScreenshot(): Promise<FeedbackImage | null> {
  const mediaDevices = navigator.mediaDevices as MediaDevices & {
    getDisplayMedia?: (constraints?: DisplayMediaStreamOptions) => Promise<MediaStream>;
  };
  if (!mediaDevices.getDisplayMedia) return null;
  const stream = await mediaDevices.getDisplayMedia({ video: true, audio: false });
  try {
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await new Promise<void>((resolve, reject) => {
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Could not read the shared screen."));
    });
    await video.play();
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not capture the shared screen.");
    ctx.drawImage(video, 0, 0, width, height);
    return {
      dataUrl: canvas.toDataURL("image/png"),
      name: feedbackImageName("screen"),
      mime: "image/png",
    };
  } finally {
    stream.getTracks().forEach((track) => track.stop());
  }
}

export function pickFeedbackImage(): Promise<FeedbackImage | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return resolve(null);
      const reader = new FileReader();
      reader.onload = () => resolve({
        dataUrl: String(reader.result),
        name: file.name || feedbackImageName("attachment"),
        mime: file.type || "image/png",
      });
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    };
    input.click();
  });
}

export async function sendFeedback(draft: FeedbackDraft): Promise<"shared" | "email-draft" | "compose-tab"> {
  const subject = tr("feedback.subject");
  const body = feedbackBody(draft);
  if (detectPlatform() === "capacitor") {
    // A mailto draft can't carry an attachment — route images through the
    // native share sheet (same plugins as file-io's export path).
    if (draft.image && (await shareMobileFeedback(subject, body, draft.image))) return "shared";
    openMobileMailDraft(mailtoUrl(subject, body));
    return "email-draft";
  }

  if (draft.image) {
    const file = dataUrlToFile(draft.image);
    const shareData: ShareData = {
      title: subject,
      text: `${body}\n\nSend to: ${FEEDBACK_EMAIL}`,
      files: [file],
    };
    if (navigator.canShare?.(shareData)) {
      await navigator.share(shareData);
      return "shared";
    }
    openFeedbackComposeTab(subject, body, draft.image);
    return "compose-tab";
  }

  openMailDraft(mailtoUrl(subject, body));
  return "email-draft";
}

function feedbackBody({ note, source, image }: FeedbackDraft): string {
  const lines = [
    note.trim() || tr("feedback.noNote"),
    "",
    "---",
    ...feedbackDiagnostics(source, image),
  ];
  return lines.join("\n");
}

function feedbackDiagnostics(source?: string, image?: FeedbackImage): string[] {
  const platform = detectPlatform();
  const settings = useSettingsStore.getState();
  const tasks = useTasksStore.getState();
  const calendars = useCalendarsStore.getState();
  const topics = useTopicsStore.getState();
  const labels = useLabelsStore.getState();
  const defaultCalendar = calendars.calendars.find((c) => c.id === settings.defaultCalendarId);
  const openTasks = tasks.tasks.filter((t) => t.status !== "done" && !t.is_event).length;
  const doneTasks = tasks.tasks.filter((t) => t.status === "done" && !t.is_event).length;
  const events = tasks.tasks.filter((t) => t.is_event).length;
  const recurring = tasks.tasks.filter((t) => t.recurrence_id).length;

  return [
    "App",
    `- Product: YourCalendar`,
    `- App version: ${__APP_VERSION__}`,
    `- Web package version: ${__WEB_VERSION__}`,
    `- Build time: ${__BUILD_TIME__}`,
    `- Runtime mode: ${import.meta.env.MODE}`,
    `- Production build: ${String(import.meta.env.PROD)}`,
    "",
    "Feedback",
    `- Source: ${source ?? "manual"}`,
    `- Created at: ${new Date().toISOString()}`,
    `- Attachment: ${image ? `${image.name} (${image.mime}, approx ${approxDataUrlBytes(image.dataUrl)} bytes)` : "none"}`,
    "",
    "Current View",
    `- URL: ${safeLocation()}`,
    `- Path: ${typeof location !== "undefined" ? location.pathname : "/"}`,
    `- Visibility: ${typeof document !== "undefined" ? document.visibilityState : "unknown"}`,
    `- Online: ${typeof navigator !== "undefined" ? String(navigator.onLine) : "unknown"}`,
    "",
    "Platform",
    `- App platform: ${platform}`,
    `- User agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "unknown"}`,
    `- Browser language: ${typeof navigator !== "undefined" ? navigator.language : "unknown"}`,
    `- Time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone ?? "unknown"}`,
    `- Viewport: ${typeof window !== "undefined" ? `${window.innerWidth}x${window.innerHeight}` : "unknown"}`,
    `- Screen: ${typeof screen !== "undefined" ? `${screen.width}x${screen.height} @ ${typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1}x` : "unknown"}`,
    "",
    "App State",
    `- Stores loaded: settings=${settings.loaded}, tasks=${tasks.loaded}, calendars=${calendars.loaded}, topics=${topics.loaded}, labels=${labels.loaded}`,
    `- Tasks: total=${tasks.tasks.length}, open=${openTasks}, done=${doneTasks}, events=${events}, recurring=${recurring}`,
    `- Recurrence rules: ${Object.keys(tasks.rules).length}`,
    `- Calendars: ${calendars.calendars.length} (hidden=${calendars.calendars.filter((c) => c.hidden).length})`,
    `- Topics: ${topics.topics.length}`,
    `- Labels: ${labels.labels.length}`,
    "",
    "Preferences",
    `- Theme: ${settings.theme}`,
    `- Language: ${settings.language}`,
    `- Time format: ${settings.timeFormat}`,
    `- Week starts on: ${settings.weekStartsOn === 0 ? "Sunday" : "Monday"}`,
    `- Week/X-Days focus: ${settings.multiDayFocus}`,
    `- Default view: ${settings.defaultView}`,
    `- Last calendar view: ${settings.lastCalendarView}`,
    `- Calendar day count: ${settings.calendarDayCount}`,
    `- Calendar hour height: ${settings.calendarHourHeight}`,
    `- Default time zone: ${settings.defaultTz ?? "floating/device-local"}`,
    `- Default calendar: ${defaultCalendar ? `${defaultCalendar.name} (${defaultCalendar.id})` : settings.defaultCalendarId ?? "none"}`,
    `- Sidebar: open=${settings.sidebarOpen}, width=${settings.sidebarWidth}`,
  ];
}

function safeLocation(): string {
  if (typeof location === "undefined") return "unknown";
  return `${location.origin}${location.pathname}${location.search}${location.hash}`;
}

function approxDataUrlBytes(dataUrl: string): number {
  const base64 = dataUrl.split(",", 2)[1] ?? "";
  return Math.round((base64.length * 3) / 4);
}

function mailtoUrl(subject: string, body: string): string {
  return `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function openMailDraft(url: string): void {
  const draftWindow = window.open("about:blank", "_blank");
  if (draftWindow) {
    draftWindow.opener = null;
    draftWindow.document.title = tr("feedback.openingTitle");
    draftWindow.document.body.textContent = tr("feedback.openingBody");
    draftWindow.location.href = url;
    return;
  }

  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.append(a);
  a.click();
  a.remove();
}

function openMobileMailDraft(url: string): void {
  window.location.href = url;
}

/** Android: write the image to the cache dir and open the system share sheet
 *  (text + file). Returns false when the plugins are unavailable so the caller
 *  can fall back to a plain mail draft. */
async function shareMobileFeedback(
  subject: string,
  body: string,
  image: FeedbackImage,
): Promise<boolean> {
  try {
    const { Filesystem, Directory } = await import("@capacitor/filesystem");
    const { Share } = await import("@capacitor/share");
    const base64 = image.dataUrl.split(",", 2)[1] ?? "";
    // No `encoding` ⇒ Capacitor decodes base64 to binary.
    const res = await Filesystem.writeFile({ path: image.name, data: base64, directory: Directory.Cache });
    try {
      await Share.share({ title: subject, text: `${body}\n\nSend to: ${FEEDBACK_EMAIL}`, files: [res.uri] });
    } catch {
      // Dismissing the share sheet rejects — that's a cancel, not an error.
    }
    return true;
  } catch {
    return false;
  }
}

function openFeedbackComposeTab(subject: string, body: string, image: FeedbackImage): void {
  const tab = window.open("about:blank", "_blank");
  if (!tab) throw new Error("Could not open feedback tab.");

  const mailto = mailtoUrl(subject, body);
  tab.opener = null;
  tab.document.open();
  tab.document.write(feedbackComposeHtml({ subject, body, image, mailto }));
  tab.document.close();
}

function feedbackComposeHtml({
  subject,
  body,
  image,
  mailto,
}: {
  subject: string;
  body: string;
  image: FeedbackImage;
  mailto: string;
}): string {
  const settings = useSettingsStore.getState();
  const textScale = settings.textScale;
  const textFont = fontStack(settings.textFont);
  const copy = {
    statusEmailOpened: tr("feedback.statusEmailOpened"),
    statusMessageCopied: tr("feedback.statusMessageCopied"),
    statusMessageSelect: tr("feedback.statusMessageSelect"),
    statusScreenshotCopied: tr("feedback.statusScreenshotCopied"),
    statusScreenshotCopyFailed: tr("feedback.statusScreenshotCopyFailed"),
  };
  const payload = JSON.stringify({ email: FEEDBACK_EMAIL, subject, body, image, mailto, copy })
    .replace(/</g, "\\u003c");
  return `<!doctype html>
<html lang="${escapeHtml(appLocale())}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${html("feedback.composeTabTitle")}</title>
  <style>
    :root { color-scheme: light dark; --app-text-scale: ${textScale}; --app-font-family: ${textFont}; font-family: var(--app-font-family); }
    body { margin: 0; background: Canvas; color: CanvasText; }
    main { box-sizing: border-box; max-width: 980px; margin: 0 auto; padding: 28px; }
    h1 { margin: 0 0 8px; font-size: calc(24px * var(--app-text-scale)); }
    p { line-height: 1.5; }
    .card { border: 1px solid color-mix(in srgb, CanvasText 18%, transparent); border-radius: 10px; padding: 16px; margin: 16px 0; background: color-mix(in srgb, Canvas 94%, CanvasText 6%); }
    .actions { display: flex; flex-wrap: wrap; gap: 10px; margin: 16px 0; }
    button, a.button { border: 1px solid color-mix(in srgb, CanvasText 24%, transparent); border-radius: 8px; padding: 10px 14px; background: Canvas; color: CanvasText; cursor: pointer; font: inherit; text-decoration: none; }
    button.primary, a.primary { background: #4f46e5; border-color: #4f46e5; color: white; }
    textarea { box-sizing: border-box; min-height: 220px; width: 100%; resize: vertical; border-radius: 8px; padding: 12px; font-family: var(--app-font-family); font-size: calc(13px * var(--app-text-scale)); line-height: 1.5; }
    img { display: block; max-width: 100%; max-height: 520px; object-fit: contain; border-radius: 8px; border: 1px solid color-mix(in srgb, CanvasText 14%, transparent); background: Canvas; }
    .muted { color: color-mix(in srgb, CanvasText 65%, transparent); }
    .status { min-height: 1.5em; font-size: calc(14px * var(--app-text-scale)); }
    code { font-family: var(--app-font-family); }
  </style>
</head>
<body>
  <main>
    <h1>${html("feedback.composeTabHeading")}</h1>
    <p class="muted">${html("feedback.composeTabIntro", { email: FEEDBACK_EMAIL })}</p>
    <div class="actions">
      <button class="primary" id="openMail">${html("feedback.openEmailApp")}</button>
      <button id="copyMessage">${html("feedback.copyMessage")}</button>
      <button id="copyImage">${html("feedback.copyScreenshot")}</button>
    </div>
    <p id="status" class="status" aria-live="polite"></p>
    <section class="card">
      <h2>${html("feedback.message")}</h2>
      <p class="muted">${html("feedback.messageHelp")}</p>
      <textarea id="body"></textarea>
    </section>
    <section class="card">
      <h2>${html("feedback.screenshot")}</h2>
      <p class="muted" id="imageName"></p>
      <img id="preview" alt="${html("feedback.screenshotAlt")}" />
    </section>
  </main>
  <script>
    const data = ${payload};
    const status = document.getElementById("status");
    const setStatus = (message) => { status.textContent = message; };
    document.getElementById("body").value = data.body;
    document.getElementById("imageName").textContent = data.image.name + " (" + data.image.mime + ")";
    document.getElementById("preview").src = data.image.dataUrl;
    document.getElementById("openMail").addEventListener("click", () => {
      const opened = window.open(data.mailto, "_blank");
      if (!opened) window.location.href = data.mailto;
      setStatus(data.copy.statusEmailOpened);
    });
    document.getElementById("copyMessage").addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(data.body);
        setStatus(data.copy.statusMessageCopied);
      } catch {
        document.getElementById("body").select();
        setStatus(data.copy.statusMessageSelect);
      }
    });
    document.getElementById("copyImage").addEventListener("click", async () => {
      try {
        const blob = await (await fetch(data.image.dataUrl)).blob();
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        setStatus(data.copy.statusScreenshotCopied);
      } catch {
        setStatus(data.copy.statusScreenshotCopyFailed);
      }
    });
  </script>
</body>
</html>`;
}

function html(key: I18nKey, vars?: Record<string, string | number | null | undefined>): string {
  return escapeHtml(tr(key, vars));
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case "\"": return "&quot;";
      default: return "&#39;";
    }
  });
}

function feedbackImageName(prefix: string): string {
  return `reminders-${prefix}-${new Date().toISOString().replace(/[:.]/g, "-")}.png`;
}

function dataUrlToFile(image: FeedbackImage): File {
  const bytes = dataUrlToBytes(image.dataUrl);
  const buffer = new ArrayBuffer(bytes.length);
  new Uint8Array(buffer).set(bytes);
  return new File([buffer], image.name, { type: image.mime });
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(",", 2)[1] ?? "";
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
